import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Slide
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle, ArrowForward } from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const statusColorMap = {
  new: 'warning',
  pending: 'warning',
  progress: 'info',
  accepted: 'success',
  rejected: 'error',
  active: 'success'
};

const beautify = (s) => {
  if (!s) return '-';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const StatusConfirmationModal = ({ open, onClose, onConfirm, title, message, currentStatus, newStatus }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? () => {} : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 60%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f0f7ff 60%)',
          boxShadow: '0 16px 40px rgba(0,0,0,0.15)'
        }
      }}
    >
      <Box sx={{
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
        color: 'white'
      }}>
        <Box sx={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <WarningIcon sx={{ fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>{title}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>{message}</Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 2.5,
          borderRadius: 2,
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          border: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          position: 'relative'
        }}>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', opacity: 0.6 }}>Current</Typography>
            <Chip
              label={beautify(currentStatus)}
              color={statusColorMap[currentStatus?.toLowerCase()] || 'default'}
              variant="outlined"
              sx={{ mt: 0.5, fontWeight: 600 }}
            />
          </Box>
          <ArrowForward sx={{ fontSize: 30, opacity: 0.35 }} />
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', opacity: 0.6 }}>New</Typography>
            <Chip
              label={beautify(newStatus)}
              color={statusColorMap[newStatus?.toLowerCase()] || 'default'}
              sx={{ mt: 0.5, fontWeight: 600 }}
            />
          </Box>
          <Box sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(circle at center, rgba(99,102,241,0.08) 0%, transparent 70%)'
          }} />
        </Box>
        <Typography variant="body2" sx={{ mt: 2.5, lineHeight: 1.5, opacity: 0.85 }}>
          Tindakan ini akan memperbarui status kandidat di sistem. Pastikan perubahan sesuai dengan alur rekrutmen internal Anda.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Batal
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} /> : <CheckCircle />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
            boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
            '&:hover': {
              background: 'linear-gradient(90deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%)'
            }
          }}
        >
          {submitting ? 'Memproses...' : 'Konfirmasi'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusConfirmationModal;